import { useEffect, useState } from "react";
import { Clock, Footprints, Mountain, Snowflake } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { weatherEmoji } from "../lib/format";
import { TAG_COLOR } from "../lib/theme";
import type { UiLang, Stop } from "../lib/types";

interface Props {
  stop: Stop | null;
  lang: UiLang;
  /** Kalles når kortet skal lukke seg selv. Se kommentaren under. */
  onDismiss: () => void;
}

/**
 * Bildekort som følger musepekeren over kart og terrengprofil.
 *
 * Bildene krever kreditering, så fotograf og lisens står under hvert bilde.
 * Kortet posisjoneres med `position: fixed` mot musepekeren, så det aldri
 * klippes av containeren.
 *
 * Kortet lukker seg selv, og det er ikke overflødig forsiktighet.
 *
 * Tidligere var det helt avhengig av at hver produsent — det skjematiske
 * kartet, 3D-markørene, høydeprofilen og tidslinja — husket å melde
 * `onHover(null)` på mouseleave. Det holder ikke i praksis: en 3D-markør som
 * fjernes mens pekeren står over den sender ingen mouseleave i det hele tatt,
 * og drar man pekeren raskt ut gjennom mellomrommet mellom to elementer, går
 * hendelsen tapt. Kortet ble stående midt på skjermen, over innholdet, til man
 * tilfeldigvis hoverte noe annet.
 *
 * Nå følger kortet pekeren selv: er den ikke over et element merket
 * `data-hover-source`, lukker det seg. Da retter en tapt hendelse seg opp ved
 * neste bevegelse, uansett hvem som mistet den.
 */

/**
 * Berøringsskjermer har ingen hover: der ville kortet dukket opp ved trykk og
 * lagt seg over innholdet brukeren nettopp trykket på. Detaljpanelet dekker
 * samme behov på mobil.
 */
function usesPointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export function StopHoverCard({ stop, lang, onDismiss }: Props) {
  const t = dictionaries[lang];
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const [enabled, setEnabled] = useState(usesPointer);

  useEffect(() => {
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setEnabled(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!stop || !enabled) return;
    setLoaded(false);
    setBroken(false);

    const move = (event: PointerEvent) => {
      setPointer({ x: event.clientX, y: event.clientY });

      /*
       * Er pekeren utenfor alt som kan vise kortet, lukkes det.
       *
       * `composedPath` framfor `target.closest` fordi markørene i 3D-kartet
       * ligger i egne noder MapLibre eier, og stien fanger dem uansett hvor de
       * er festet. Kortet selv har `pointer-events: none`, så det kan ikke
       * havne i stien og lukke seg fordi pekeren traff det.
       */
      const overSource = event
        .composedPath()
        .some((node) => node instanceof Element && node.hasAttribute("data-hover-source"));

      if (!overSource) onDismiss();
    };

    // Et trykk, en rulling eller pekeren ut av vinduet lukker også kortet.
    const leave = () => onDismiss();

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerdown", leave);
    window.addEventListener("blur", leave);
    // `capture`, så det fanges uansett hvilken container som ruller.
    window.addEventListener("scroll", leave, true);
    document.addEventListener("pointerleave", leave);

    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerdown", leave);
      window.removeEventListener("blur", leave);
      window.removeEventListener("scroll", leave, true);
      document.removeEventListener("pointerleave", leave);
    };
  }, [stop, enabled, onDismiss]);

  if (!stop || !enabled) return null;

  // Snu kortet når pekeren nærmer seg kanten, så det aldri havner utenfor.
  const flipX = pointer.x > window.innerWidth - 320;
  const flipY = pointer.y > window.innerHeight - 300;

  return (
    <div
      className="hover-card no-print"
      style={{
        left: flipX ? pointer.x - 300 : pointer.x + 18,
        top: flipY ? pointer.y - 280 : pointer.y + 18,
      }}
      role="tooltip"
    >
      {stop.image && !broken && (
        <div className="hover-image">
          <img
            src={stop.image.src}
            alt={stop.name}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
            style={{ opacity: loaded ? 1 : 0 }}
          />
        </div>
      )}

      <div className="hover-body">
        <div className="hover-head">
          <strong>{stop.name}</strong>
          {/*
            Hva slags sted dette er, ikke bare hva det heter.
            
            Kortet viste navn, høyde og beskrivelse, og for et sted brukeren
            vurderer å legge inn i ruta er «fjord» eller «by» det raskeste
            svaret på det de egentlig lurer på. Taggen fantes i dataene og
            hadde bare en farge på kartet.
          */}
          <span className="hover-tag" style={{ borderColor: TAG_COLOR[stop.tags[0]] }}>
            {t.tagName[stop.tags[0]]}
          </span>
          <span className="hover-elevation">
            <Mountain size={11} /> {stop.elevationM} {t.unitMasl}
          </span>
        </div>

        <p className="hover-blurb">{stop.blurb}</p>

        {/*
          Tre opplysninger som avgjør om stedet passer i dagen: hvor lenge man
          blir, om siste etappe går til fots, og om vinteren endrer noe. De to
          siste er de som overrasker — en tolv timers fottur eller en
          vinterstengt vei er ikke noe man gjetter fra et navn.
        */}
        <div className="hover-facts">
          <span>
            <Clock size={11} /> {t.addStopDwell(stop.dwellHours)}
          </span>
          {stop.hike && (
            <span>
              <Footprints size={11} /> {t.addStopHike(stop.hike.hoursMax)}
            </span>
          )}
          {stop.winter?.access && (
            <span className={`hover-winter hover-winter--${stop.winter.access.status}`}>
              <Snowflake size={11} /> {t.winterStatus[stop.winter.access.status]}
            </span>
          )}
        </div>

        {/* Hva man gjør her om vinteren, kort. Panelet i drawer har det hele. */}
        {stop.winter && stop.winter.activities.length > 0 && (
          <p className="hover-winter-offer">
            {stop.winter.activities
              .slice(0, 3)
              .map((activity) => activity.name)
              .join(" · ")}
          </p>
        )}

        {stop.weather && (
          <div className="hover-weather">
            <span className="weather-emoji">{weatherEmoji(stop.weather.symbol)}</span>
            {Math.round(stop.weather.tempMin)}–{Math.round(stop.weather.tempMax)} °C
            {stop.viewScore && (
              <span className="hover-score">
                {t.viewIndexShort} {stop.viewScore.score}
              </span>
            )}
          </div>
        )}

        {/*
          Klimanormalen, der det ikke finnes noe varsel. Merket som anslag ved
          tallet: et anslag på 58 og et varsel på 58 er ikke samme påstand, og
          skillet må stå der tallet står. Se services/outlook.ts.
        */}
        {!stop.weather && stop.outlook && (
          <div className="hover-weather hover-weather--estimate">
            {stop.outlook.tMin}–{stop.outlook.tMax} °C
            <span className="hover-score">
              {t.viewIndexShort} {stop.outlook.score}
            </span>
            <span className="hover-estimate">{t.outlookEstimate}</span>
          </div>
        )}

        {stop.image && !broken && (
          <div className="hover-credit">
            {t.photoBy} {stop.image.credit} · {stop.image.license}
          </div>
        )}
      </div>
    </div>
  );
}
